import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  getTeamList,
  createTeam,
  updateTeam,
  deleteTeam,
} from "../api/team.api";
import type {
  TeamPayload,
  GetTeamListParams,
} from "@/interface/masterManagement";
import { teamKeys, STALE_5_MIN } from "@/lib/queryKey";
import { getNextPage, isPaginated } from "@/utils/helper";

export const useTeamList = (params: GetTeamListParams) =>
  useQuery({
    queryKey: teamKeys.list(params),
    queryFn: () => getTeamList(params),
    ...STALE_5_MIN,
  });

export const useInfiniteTeamList = (params?: GetTeamListParams) => {
  const hasPagination = isPaginated(params);

  return useInfiniteQuery({
    queryKey: teamKeys.list({ ...params, type: "infinite" }),
    queryFn: ({ pageParam }) =>
      getTeamList(
        hasPagination ? { ...params, page: pageParam } : params,
      ),
    initialPageParam: params?.page ?? 1,
    getNextPageParam: (lastPage, allPages) =>
      hasPagination
        ? getNextPage(lastPage, allPages, params?.page_size)
        : undefined,
    ...STALE_5_MIN,
  });
};

export const useCreateTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTeam,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.lists() });
    },
  });
};

export const useUpdateTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number | string;
      payload: TeamPayload;
    }) => updateTeam(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.lists() });
    },
  });
};

export const useDeleteTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => deleteTeam(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.lists() });
    },
  });
};
